import { useCallback, useEffect, useMemo, useState } from 'react'
import { DateRange } from 'react-day-picker'

import { getSpeedOfServiceMetrics } from '@/api/clarity'
import { formatMetric } from '@/hooks/clarity/util'
import { SpeedRegionBoxMetricsModel } from '@/types/clarity'
import { dateToString, getMappedRegionBranches } from '@/utils'
import { DateType, getTrendingDate } from '@/utils/date'

export default function useSpeedRegionBoxMetrics(
  startDate: string,
  endDate: string,
  dateFilterType: DateType
) {
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)
  const [branchData, setBranchData] = useState<SpeedRegionBoxMetricsModel[]>([])
  const [branchTrendData, setBranchTrendData] = useState<
    SpeedRegionBoxMetricsModel[]
  >([])
  const { regionBranchMap, branches } = getMappedRegionBranches()
  const [processedData, setProcessedData] = useState<
    SpeedRegionBoxMetricsModel[]
  >([])

  const fetchData = useMemo(() => {
    return async () => {
      try {
        const { branchMetrics } = await fetchSpeedRegionBoxMetrics(
          branches,
          startDate,
          endDate,
          regionBranchMap
        )

        const filteredBranchMetrics = branchMetrics.filter(
          (metric) =>
            metric.stand !== 'system_total' && metric.region !== 'Other'
        )

        setBranchData(filteredBranchMetrics)
      } catch (err) {
        setError(
          new Error(
            err instanceof Error ? err.message : 'An unknown error occurred'
          )
        )
      }
    }
  }, [startDate, endDate])

  const fetchTrendData = useMemo(() => {
    return async () => {
      try {
        const trendingDate = getTrendingDate(
          { from: new Date(startDate), to: new Date(endDate) },
          dateFilterType
        ) as DateRange

        const { branchMetrics } = await fetchSpeedRegionBoxMetrics(
          branches,
          dateToString(trendingDate, 'from'),
          dateToString(trendingDate, 'to'),
          regionBranchMap
        )

        const filteredBranchMetrics = branchMetrics.filter(
          (metric) => metric.stand !== 'system_total'
        )

        setBranchTrendData(filteredBranchMetrics)
      } catch (err) {
        setError(
          new Error(
            err instanceof Error ? err.message : 'An unknown error occurred'
          )
        )
      }
    }
  }, [startDate, endDate, dateFilterType])

  const getProcessedData = useCallback(
    (
      currentData: SpeedRegionBoxMetricsModel[],
      trendData: SpeedRegionBoxMetricsModel[]
    ) => {
      if (!currentData.length || !trendData.length) return []

      const currentDataMap = new Map(
        currentData.map((item) => [item.stand, item])
      )
      const trendDataMap = new Map(trendData.map((item) => [item.stand, item]))
      const commonStands = [...currentDataMap.keys()].filter((stand) =>
        trendDataMap.has(stand)
      )

      const standsByRegion = commonStands.reduce((acc, stand) => {
        const region = currentDataMap.get(stand)?.region || ''
        const current = currentDataMap.get(stand)?.current
        const trend = trendDataMap.get(stand)?.current

        if (!acc.has(region)) {
          acc.set(region, [])
        }

        acc.get(region)?.push({
          stand: stand,
          region: region,
          current: {
            speedOfService: current?.speedOfService || 0,
            utilization: current?.utilization || 0,
            windowTotal: current?.windowTotal || 0,
            serviceTotal: current?.serviceTotal || 0,
          },
          prev: {
            speedOfService: trend?.speedOfService || 0,
            utilization: trend?.utilization || 0,
            windowTotal: trend?.windowTotal || 0,
            serviceTotal: trend?.serviceTotal || 0,
          },
        })

        return acc
      }, new Map<string, SpeedRegionBoxMetricsModel[]>())

      const sortedStandsByRegion = new Map(
        [...standsByRegion.entries()].sort((a, b) => {
          const regionA = a[0].toLowerCase()
          const regionB = b[0].toLowerCase()
          return regionA.localeCompare(regionB)
        })
      )

      // Create parent regions with children stands
      const processedData = Array.from(sortedStandsByRegion.entries()).map(
        ([region, stands]) => {
          // Create parent region with aggregated metrics

          const calculateAverage = (
            stands: SpeedRegionBoxMetricsModel[],
            key: keyof SpeedRegionBoxMetricsModel['current']
          ) =>
            Number(
              (
                stands.reduce(
                  (sum, stand) => sum + (stand.current?.[key] || 0),
                  0
                ) / stands.length
              ).toFixed(2)
            )

          const calculatePreviousAverage = (
            stands: SpeedRegionBoxMetricsModel[],
            key: keyof SpeedRegionBoxMetricsModel['prev']
          ) =>
            Number(
              (
                stands.reduce(
                  (sum, stand) => sum + (stand.prev?.[key] || 0),
                  0
                ) / stands.length
              ).toFixed(2)
            )

          const regionData: SpeedRegionBoxMetricsModel = {
            stand: region,
            current: {
              speedOfService: calculateAverage(
                stands,
                'speedOfService' as never
              ),
              utilization: calculateAverage(stands, 'utilization' as never),
              windowTotal: calculateAverage(stands, 'windowTotal' as never),
              serviceTotal: calculateAverage(stands, 'serviceTotal' as never),
            },
            prev: {
              speedOfService: calculatePreviousAverage(
                stands,
                'speedOfService' as never
              ),
              utilization: calculatePreviousAverage(
                stands,
                'utilization' as never
              ),
              windowTotal: calculatePreviousAverage(
                stands,
                'windowTotal' as never
              ),
              serviceTotal: calculatePreviousAverage(
                stands,
                'serviceTotal' as never
              ),
            },
            children: stands,
          }

          return regionData
        }
      )

      setProcessedData(processedData as SpeedRegionBoxMetricsModel[])
    },
    [branchData, branchTrendData]
  )

  const fetchAllData = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      await Promise.all([fetchData(), fetchTrendData()])
    } catch (err) {
      setError(
        new Error(
          err instanceof Error ? err.message : 'An unknown error occurred'
        )
      )
    } finally {
      setLoading(false)
    }
  }, [fetchData, fetchTrendData])

  const refetch = useCallback(() => {
    console.log('Refetching speed region box data...')
    return fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    if (branchData.length && branchTrendData.length) {
      getProcessedData(branchData, branchTrendData)
    }
  }, [branchData, branchTrendData])

  return {
    processedData,
    loading,
    error,
    refetch,
  }
}

async function fetchSpeedRegionBoxMetrics(
  branches: string[],
  startDate: string,
  endDate: string,
  regionBranchMap: Map<string, string[]>
): Promise<{
  branchMetrics: SpeedRegionBoxMetricsModel[]
}> {
  try {
    const response = await getSpeedOfServiceMetrics(
      branches.join(','),
      startDate,
      endDate
    )

    const metrics: SpeedRegionBoxMetricsModel[] = response.map(
      (responseMetrics) =>
        setMetric(
          responseMetrics.stand,
          getBranchRegion(responseMetrics.stand, regionBranchMap),
          responseMetrics.speedOfService,
          responseMetrics.utilization,
          responseMetrics.windowTotal,
          responseMetrics.serviceTotal
        )
    )

    const branchMetrics = metrics

    return {
      branchMetrics,
    }
  } catch (err) {
    throw new Error(
      err instanceof Error ? err.message : 'An unknown error occurred'
    )
  }
}

function getBranchRegion(
  branchName: string,
  regionMap: Map<string, string[]>
): string {
  for (const [region, branches] of regionMap.entries()) {
    if (branches.includes(branchName)) {
      return region
    }
  }
  return 'Undefined Region'
}

function setMetric(
  stand: string,
  region: string,
  speedOfService: number,
  utilization: number,
  windowTotal: number,
  serviceTotal: number
): SpeedRegionBoxMetricsModel {
  return {
    stand: stand,
    region: region,
    current: {
      speedOfService: formatMetric(speedOfService, 1),
      utilization: formatMetric(utilization, 1),
      windowTotal: formatMetric(windowTotal, 0),
      serviceTotal: formatMetric(serviceTotal, 0),
    },
  }
}
